import { diningTablesAPI, type TableOrderDraftPayload } from 'api/diningTables'
import { checkConnectivity } from 'utils/connectivityCheck'
import * as tableOps from 'store/tableOpsQueue'
import type { TableOp, TableSettleOp } from 'store/tableOpsQueue'
import * as posStore from 'store/posStore'

export type FlushTableOpsResult = {
  flushed: number
  errors: string[]
  conflicts: number
}

function saleFromResponse(data: unknown): {
  id?: number | string
  updated_at?: string
  print_sequence?: number | string
  invoice_number?: string
  status?: string
} | null {
  if (!data || typeof data !== 'object') return null
  const rec = data as Record<string, unknown>
  const inner = rec.data && typeof rec.data === 'object' ? (rec.data as Record<string, unknown>) : rec
  return {
    id: inner.id as number | string | undefined,
    updated_at: inner.updated_at as string | undefined,
    print_sequence: inner.print_sequence as number | string | undefined,
    invoice_number: inner.invoice_number as string | undefined,
    status: inner.status as string | undefined,
  }
}

function isSaleCompletedStatus(status: string | undefined): boolean {
  return status === 'completed' || status === 'refunded' || status === 'cancelled' || status === 'merged'
}

function isNoActiveOrderError(err: unknown): boolean {
  const e = err as { response?: { status?: number; data?: { message?: string } } }
  const msg = e?.response?.data?.message ?? ''
  return (
    e?.response?.status === 422 &&
    (msg.includes('لا يوجد طلب نشط') || msg.includes('no active order'))
  )
}

async function finalizeTableAfterCheckout(tableId: string): Promise<void> {
  await tableOps.clearTableLocalMeta(tableId)
  await posStore.markTableLocallyAvailable(tableId)
}

/** Settle/draft already applied — safe to drop queued op after local checkout. */
async function tryCompleteTableOpIdempotent(tableId: string): Promise<boolean> {
  const tomb = await tableOps.getTableCheckoutTombstone(tableId)
  if (tomb) {
    await finalizeTableAfterCheckout(tableId)
    return true
  }
  try {
    const active = await diningTablesAPI.getActiveOrder(tableId)
    if (active.status === 'success' && !active.data) {
      await finalizeTableAfterCheckout(tableId)
      return true
    }
  } catch {
    /* best-effort */
  }
  return false
}

async function applyDraftSuccess(tableId: string, res: unknown): Promise<void> {
  const tomb = await tableOps.getTableCheckoutTombstone(tableId)
  if (tomb) return
  const sale = saleFromResponse(res)
  if (!sale?.id) return
  await tableOps.setTableLocalMeta(tableId, {
    orderId: sale.id,
    saleUpdatedAt: sale.updated_at ?? null,
    printSequence: sale.print_sequence ?? null,
    invoiceNumber: sale.invoice_number ?? null,
  })
  await posStore.markTableLocallyOccupied(tableId)
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('pos:table-meta-updated', { detail: { tableId } }))
  }
}

function isTableOrderConflict(err: unknown): boolean {
  const e = err as { response?: { status?: number; data?: { code?: string; data?: unknown } } }
  return e?.response?.status === 409 && e?.response?.data?.code === 'table_order_conflict'
}

/** Push draft; retry on stale expected (server updated_at, then null when client_op_id present). */
async function syncTableDraftToServer(
  tableId: string,
  payload: TableOrderDraftPayload,
): Promise<unknown> {
  try {
    return await diningTablesAPI.syncOrderDraft(tableId, payload)
  } catch (firstErr: unknown) {
    if (!isTableOrderConflict(firstErr)) throw firstErr
    const sale = saleFromResponse(
      (firstErr as { response?: { data?: { data?: unknown } } })?.response?.data?.data ??
        (firstErr as { response?: { data?: unknown } })?.response?.data,
    )
    if (sale?.updated_at) {
      try {
        const retryPayload: TableOrderDraftPayload = {
          ...payload,
          expected_sale_updated_at: sale.updated_at,
        }
        return await diningTablesAPI.syncOrderDraft(tableId, retryPayload)
      } catch (secondErr: unknown) {
        if (!isTableOrderConflict(secondErr) || !payload.client_op_id) throw secondErr
        const forcePayload: TableOrderDraftPayload = {
          ...payload,
          expected_sale_updated_at: null,
        }
        return diningTablesAPI.syncOrderDraft(tableId, forcePayload)
      }
    }
    if (payload.client_op_id) {
      const forcePayload: TableOrderDraftPayload = {
        ...payload,
        expected_sale_updated_at: null,
      }
      return diningTablesAPI.syncOrderDraft(tableId, forcePayload)
    }
    throw firstErr
  }
}

async function flushOneOp(op: TableOp, queue: TableOp[]): Promise<{ ok: boolean; error?: string; conflict?: boolean }> {
  try {
    switch (op.op) {
      case 'draft': {
        const tomb = await tableOps.getTableCheckoutTombstone(op.table_id)
        if (tomb) {
          await finalizeTableAfterCheckout(op.table_id)
          return { ok: true }
        }
        const meta = await tableOps.getTableLocalMeta(op.table_id)
        const payload: TableOrderDraftPayload = {
          ...op.payload,
          client_op_id: op.client_op_id ?? op.id,
          expected_sale_updated_at: op.enqueued_while_offline
            ? null
            : meta?.saleUpdatedAt ?? null,
        }
        const res = await syncTableDraftToServer(op.table_id, payload)
        await applyDraftSuccess(op.table_id, res)
        return { ok: true }
      }
      case 'settle': {
        if (tableOps.settleDependsOnDraft(op, queue)) {
          return { ok: false, error: 'draft_not_synced_yet' }
        }
        let res: unknown
        try {
          res = await diningTablesAPI.settleOrder(op.table_id, {
            ...op.payload,
            client_op_id: op.payload.client_op_id ?? op.id,
          })
        } catch (settleErr: unknown) {
          if (await tryCompleteTableOpIdempotent(op.table_id)) {
            return { ok: true }
          }
          const se = settleErr as { response?: { status?: number } }
          if (se?.response?.status === 409) {
            try {
              const active = await diningTablesAPI.getActiveOrder(op.table_id)
              if (active.status === 'success' && !active.data) {
                await finalizeTableAfterCheckout(op.table_id)
                return { ok: true }
              }
            } catch {
              /* fall through */
            }
          }
          throw settleErr
        }
        const sale = saleFromResponse(res)
        await finalizeTableAfterCheckout(op.table_id)
        if (sale?.id) {
          try {
            await diningTablesAPI.releaseForPos(op.table_id)
          } catch {
            /* release best-effort */
          }
        }
        return { ok: true }
      }
      case 'transfer': {
        const res = await diningTablesAPI.transferOrder(op.source_id, op.target_id)
        await tableOps.clearTableLocalMeta(op.source_id)
        await posStore.markTableLocallyAvailable(op.source_id)
        await posStore.markTableLocallyOccupied(op.target_id)
        const transferSale = saleFromResponse(res)
        if (transferSale?.id) {
          await tableOps.setTableLocalMeta(op.target_id, {
            orderId: transferSale.id,
            saleUpdatedAt: transferSale.updated_at ?? null,
            printSequence: transferSale.print_sequence ?? null,
            invoiceNumber: transferSale.invoice_number ?? null,
          })
        }
        return { ok: true }
      }
      case 'merge': {
        const res = await diningTablesAPI.mergeOrder(op.source_id, op.target_id)
        await tableOps.clearTableLocalMeta(op.source_id)
        await tableOps.clearLocalTableMergeForSource(op.source_id)
        await posStore.markTableLocallyAvailable(op.source_id)
        await posStore.markTableLocallyOccupied(op.target_id)
        const mergeSale = saleFromResponse(res)
        if (mergeSale?.id) {
          await tableOps.setTableLocalMeta(op.target_id, {
            orderId: mergeSale.id,
            saleUpdatedAt: mergeSale.updated_at ?? null,
            printSequence: mergeSale.print_sequence ?? null,
            invoiceNumber: mergeSale.invoice_number ?? null,
          })
        }
        return { ok: true }
      }
      case 'unmerge': {
        await diningTablesAPI.unmergeOrder(op.target_id, op.source_id)
        await tableOps.clearLocalTableMergeForSource(op.source_id)
        await tableOps.clearTableLocalMeta(op.source_id)
        return { ok: true }
      }
      case 'release': {
        await diningTablesAPI.releaseForPos(op.table_id)
        await finalizeTableAfterCheckout(op.table_id)
        return { ok: true }
      }
      default:
        return { ok: false, error: 'unknown_op' }
    }
  } catch (e: unknown) {
    const err = e as { response?: { status?: number; data?: { code?: string; message?: string; data?: unknown } } }
    const tableIdForSettle =
      op.op === 'draft' || op.op === 'settle' || op.op === 'release' ? op.table_id : null

    if (tableIdForSettle && (op.op === 'settle' || op.op === 'draft') && isNoActiveOrderError(e)) {
      if (await tryCompleteTableOpIdempotent(tableIdForSettle)) {
        return { ok: true }
      }
    }

      if (err?.response?.status === 409 && err?.response?.data?.code === 'table_order_conflict') {
      const sale = saleFromResponse(err.response?.data?.data ?? err.response?.data)
      const tableId =
        op.op === 'draft' || op.op === 'settle' || op.op === 'release'
          ? op.table_id
          : op.op === 'transfer'
            ? op.source_id
            : op.op === 'merge'
              ? op.target_id
              : op.op === 'unmerge'
                ? op.target_id
                : null

      if (tableId && op.op === 'draft') {
        const tomb = await tableOps.getTableCheckoutTombstone(tableId)
        if (tomb || isSaleCompletedStatus(sale?.status)) {
          await finalizeTableAfterCheckout(tableId)
          return { ok: true }
        }
      }

      if (tableId && op.op === 'settle') {
        if (await tryCompleteTableOpIdempotent(tableId)) {
          return { ok: true }
        }
      }

      if (sale?.id && tableId && op.op !== 'draft') {
        const tomb = await tableOps.getTableCheckoutTombstone(tableId)
        if (!tomb) {
          await tableOps.setTableLocalMeta(tableId, {
            orderId: sale.id,
            saleUpdatedAt: sale.updated_at ?? null,
            printSequence: sale.print_sequence ?? null,
            invoiceNumber: sale.invoice_number ?? null,
          })
        }
      } else if (sale?.id && tableId && op.op === 'draft') {
        const tomb = await tableOps.getTableCheckoutTombstone(tableId)
        if (!tomb) {
          await tableOps.setTableLocalMeta(tableId, {
            orderId: sale.id,
            saleUpdatedAt: sale.updated_at ?? null,
            printSequence: sale.print_sequence ?? null,
            invoiceNumber: sale.invoice_number ?? null,
          })
        }
      }

      return {
        ok: false,
        conflict: true,
        error: err?.response?.data?.message || 'تعارض طلب الطاولة مع جهاز آخر',
      }
    }

    if (tableIdForSettle && op.op === 'settle' && isNoActiveOrderError(e)) {
      if (await tryCompleteTableOpIdempotent(tableIdForSettle)) {
        return { ok: true }
      }
    }

    const msg =
      err?.response?.data?.message ||
      (e instanceof Error ? e.message : 'تعذر مزامنة عملية الطاولة')
    return { ok: false, error: msg }
  }
}

export async function flushTableOps(): Promise<FlushTableOpsResult> {
  if (!(await checkConnectivity())) {
    return { flushed: 0, errors: [], conflicts: 0 }
  }

  await tableOps.resetStuckSyncingOps()

  let flushed = 0
  const errors: string[] = []
  let conflicts = 0
  const maxPasses = 50
  let pass = 0

  while (pass < maxPasses) {
    pass += 1
    const failedOps = await tableOps.getFailedTableOps()
    const failedDraftIds = tableOps.failedDraftTableIds(failedOps)
    const queue = await tableOps.getTableOpsQueue()
    const pending = queue.filter((o) => o.status === 'pending')
    if (pending.length === 0) break

    let progressed = false
    for (const op of pending) {
      if (
        op.op === 'settle' &&
        tableOps.settleDependsOnDraft(op as TableSettleOp, queue, failedDraftIds)
      ) {
        continue
      }
      if (tableOps.isStructuralTableOp(op)) {
        if (tableOps.structuralOpDependsOnDraft(op, queue)) continue
        if (tableOps.structuralOpBlockedByFailedDraft(op, failedDraftIds)) continue
      }

      const tableId =
        op.op === 'draft' || op.op === 'settle' || op.op === 'release' ? op.table_id : null
      if (tableId) {
        const tomb = await tableOps.getTableCheckoutTombstone(tableId)
        if (tomb && (op.op === 'draft' || op.op === 'settle')) {
          await tableOps.removeTableOpById(op.id)
          flushed += 1
          progressed = true
          continue
        }
      }

      await tableOps.updateTableOpInQueue(op.id, { status: 'syncing' })
      const result = await flushOneOp(op, queue)

      if (result.ok) {
        await tableOps.removeTableOpById(op.id)
        flushed += 1
        progressed = true
        continue
      }

      if (result.error === 'draft_not_synced_yet') {
        await tableOps.updateTableOpInQueue(op.id, { status: 'pending' })
        continue
      }

      await tableOps.updateTableOpInQueue(op.id, {
        status: result.conflict ? 'conflict' : 'failed',
        last_error: result.error ?? null,
        retry_count: (op.retry_count ?? 0) + 1,
      })

      if (result.conflict) {
        conflicts += 1
        await tableOps.appendFailedTableOp(
          { ...op, status: 'conflict', last_error: result.error ?? null },
          result.error ?? 'تعارض',
        )
        await tableOps.removeTableOpById(op.id)
        errors.push(`${op.op}@${op.id}: ${result.error}`)
      } else {
        await tableOps.appendFailedTableOp(
          { ...op, status: 'failed', last_error: result.error ?? null },
          result.error ?? 'فشل',
        )
        await tableOps.removeTableOpById(op.id)
        errors.push(`${op.op}@${op.id}: ${result.error}`)
      }
      progressed = true
    }

    if (!progressed) break
  }

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('pos:table-ops-flushed', { detail: { flushed, conflicts } }))
  }

  return { flushed, errors, conflicts }
}

export async function getTableOpsAttentionCount(): Promise<{
  pending: number
  failed: number
}> {
  const [pending, failed] = await Promise.all([
    tableOps.getPendingTableOpsCount(),
    tableOps.getFailedTableOps().then((l) => l.length),
  ])
  return { pending, failed }
}
